import { Alert, DialogContent, DialogContentText } from '@mui/material'
import type { FC } from 'react'
import { useState } from 'react'
import * as React from 'react'

import ConfirmDialogActions from '@/components/feedback/ConfirmDialogActions'
import CustomDialog from '@/components/feedback/CustomDialog'
import CustomButton from '@/components/inputs/CustomButton'

type Props = {
  children: React.ReactNode
  buttonProps: {
    disabled?: boolean
    startIcon: React.ReactNode
  }
  dialogProps: {
    title: string
    description: string
    warning?: string
  }
  isLoading?: boolean
  onSubmit: () => void | Promise<void>
}

const ConfirmationButton: FC<Props> = ({ children, buttonProps, dialogProps, isLoading = false, onSubmit }) => {
  const [showAlert, setShowAlert] = useState(false)
  const [isInternalLoading, setIsInternalLoading] = useState(false)

  const isCombinedLoading = isLoading || isInternalLoading

  function handleOpenAlert() {
    setShowAlert(true)
  }

  function handleCloseAlert() {
    setShowAlert(false)
  }

  async function handleConfirmAlert() {
    setIsInternalLoading(true)
    try {
      await onSubmit()
      setShowAlert(false)
    } catch {
      // Error is handled by parent/hook
    } finally {
      setIsInternalLoading(false)
    }
  }

  return (
    <>
      <CustomButton
        variant={'contained'}
        {...buttonProps}
        disabled={buttonProps.disabled || isCombinedLoading}
        onClick={handleOpenAlert}
      >
        {children}
      </CustomButton>

      <CustomDialog
        aria-describedby={dialogProps.description}
        aria-labelledby={dialogProps.title}
        open={showAlert}
        title={dialogProps.title}
        onClose={handleCloseAlert}
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{dialogProps.description}</DialogContentText>
          {dialogProps.warning !== undefined && (
            <Alert data-testid="confirmation-dialog-warning" severity="warning" sx={{ mt: 2 }}>
              {dialogProps.warning}
            </Alert>
          )}
        </DialogContent>

        <ConfirmDialogActions disabled={isCombinedLoading} onCancel={handleCloseAlert} onConfirm={handleConfirmAlert} />
      </CustomDialog>
    </>
  )
}

export default ConfirmationButton
